import _ from 'underscore';
import Environment from '../environment';
import { hamletApi } from './hamlet-api';

const ProductKey = [];
ProductKey["CONFLUENCE"] = "confluence.ondemand";
ProductKey["JIRA_CORE"] = "jira-core.ondemand";
ProductKey["JIRA_BUSINESS"] = "jira-core.ondemand";
ProductKey["JIRA_SOFTWARE"] = "jira-software.ondemand";
ProductKey["JIRA_SERVICE_DESK"] = "jira-servicedesk.ondemand";
ProductKey["BITBUCKET"] = "com.atlassian.bitbucket";
ProductKey["OPSGENIE"] = "opsgenie";
ProductKey["STATUSPAGE"] = "statuspage";
ProductKey["TRELLO"] = "trello";

export const transformAvailableProductsIntoAvailableSites = (availableProductsSites) => {
  return availableProductsSites.map(site => {
    return {
      adminAccess: site.adminAccess,
      products: site.availableProducts.map(availableProduct => ProductKey[availableProduct.productType]),
      cloudId: site.cloudId,
      displayName: site.displayName,
      avatarUrl: site.avatar,
      url: site.url,
    };
  });
}

export class HamletUser {

  constructor() {

    this.token = null;
    this.initialized = false;

    this.user = {
      contactDetails: {
        firstName: '',
        lastName: '',
        email: ''
      },
      organisationDetails: {
        address1: '',
        address2: '',
        city: '',
        state: '',
        postcode: '',
        isoCountryCode: '',
        organisationName: '',
        organisationType: null,
        payOnAccount: false,
        taxId: ''
      },
      lassoDetails: {
        firstName: '',
        lastName: '',
        username: '',
        displayName: ''
      },
      myDetails: {
        aaid: null,
        avatar: null,
        email: '',
        name: '',
        firstName: '',
        lastName: ''
      },
      isLoggedIn: false
    };

    this.sites = [];
    this.performanceBoostExperiment = false;

  }

  initUser(performanceBoostExperiment, enableAisAvailableProducts) {

    if (this.initialized) {
      return Promise.resolve(this.user);
    }
    this.initialized = true;

    return Promise.all([
      this.retrieveXsrfToken(),
      this.getStargateProfile(performanceBoostExperiment, false, enableAisAvailableProducts)
    ])
      .then(() => {
        return Promise.resolve(this.user);
      })
      .catch((e) => {
        return Promise.resolve(this.user);
      });
  }

  retrieveXsrfToken() {

    const options = {
      url: `${hamletApi.getHamletPath()}/1.0/auth/xsrf/plain`,
      method: 'GET',
      withCredentials: true
    };

    return hamletApi.request(options)
      .then((response) => {

        if (response.data && response.data !== '') {

          this.setToken(response.data);
          this.user.isLoggedIn = true;

          return Promise.resolve();
        }

        this.user.isLoggedIn = false;
        return Promise.reject();
      })
      .catch((e) => {

        this.user.isLoggedIn = false;
        return Promise.reject();
      });
  }

  getStargateProfile(performanceBoostExperiment, debugAllSites, enableAisAvailableProducts = false) {

    if (performanceBoostExperiment) {
      this.performanceBoostExperiment = true;
    }

    return Promise.all([
      this.retrieveMe(),
      this.retrieveSitesData(debugAllSites, enableAisAvailableProducts)
    ])
      .then(([identityData, sitesData]) => {

        if (identityData && sitesData) {
          _.extend(this.user.myDetails, identityData);
          this.setSites(sitesData);
          this.user.isLoggedIn = true;
        }

        return Promise.resolve();
      })
      .catch((e) => {

        this.user.isLoggedIn = false;
        return Promise.resolve();
      });
  }

  retrieveCurrentUser(params) {
    params.url = '/1.0/public/contact/currentUser';

    return hamletApi.hamletRequest(params);
  }

  retrieveUserDetails(params) {
    params.url = '/1.0/public/auth/user/details'

    return hamletApi.hamletRequest(params);
  }

  retrieveMe() {
    const hostname = Environment.getAPIPrivateURL();
    const url = `${hostname}/me`;

    const options = {
      url: url,
      method: 'GET',
      withCredentials: true
    };

    return hamletApi.request(options)
      .then(result => {

        if (!result.data) {
          return Promise.reject();
        }

        // clean up any names that are emails here
        let name = result.data.name;
        if (name.indexOf('@') > -1) {
          name = name.split('@')[0];
        }

        const stargateData = {
          aaid: (result.data.account_id),
          avatar: (result.data.picture),
          email: (result.data.email),
          name: (name),
          firstName: (this.getFirstName(name)),
          lastName: (this.getLastName(name))
        };

        return Promise.resolve(stargateData);
      })
      .catch(e => {

        this.user.isLoggedIn = false;
        return Promise.reject();
      });
  }

  retrieveSitesData(debugAllSites, enableAisAvailableProducts) {
    return this.retrieveAvailableProductsData(debugAllSites, enableAisAvailableProducts).then(availableProductsSites => {
      const sitesData = transformAvailableProductsIntoAvailableSites(availableProductsSites);
      return sitesData;
    }).catch(() => {
      this.user.isLoggedIn = false;
    });
  }

  retrieveAvailableProductsData(debugAllSites, enableAisAvailableProducts) {
    let url = `${Environment.getAPIPrivateURL()}`;
    if (enableAisAvailableProducts) {
      url = url + `/ais/available-products`
    } else {
      url = url + `/available-products/api/available-products`
    }
    const options = {
      url: url,
      method: 'GET',
      withCredentials: true
    };

    return (hamletApi.request(options)).then(result => {

      if (!result.data) {
        return [];
      }
      const sitesResult = result.data.sites || [];
      const availableProductsSites = debugAllSites ? sitesResult : sitesResult.filter((site) => this.isValidSite(site));
      return Promise.resolve(availableProductsSites);
    }).catch(e => {
      this.user.isLoggedIn = false;
      return Promise.reject();
    });
  }

  checkExtendedTrialAvailability(migrationSourceUuid, productKey, cloudIds) {
    const options = {
      url: `${hamletApi.getHamletPath()}/1.0/public/account/${migrationSourceUuid}/extendedTrial/availability`,
      method: 'POST',
      data: {
        productKey,
        cloudIds,
      },
      withCredentials: true
    };

    return hamletApi.request(options)
      .then((response) => {
        if (response.data && response.data !== '') {
          this.setExtendedTrialAvailability(response.data);
          return Promise.resolve(response.data);
        }

        return Promise.resolve();
      })
      .catch((e) => {
        return Promise.resolve();
      });
  }

  getExtendedTrialAvailability() {
    return this.extendedTrialAvailability;
  }

  setExtendedTrialAvailability(extendedTrialAvailability) {
    this.extendedTrialAvailability = extendedTrialAvailability;
  }

  setToken(token) {
    this.token = token;
  }

  getToken() {
    return this.token;
  }

  setUser(options) {
    _.extend(this.user, options);
  }

  getUser() {
    return this.user;
  }

  setSites(sites) {
    this.sites = sites;
  }

  getSites() {
    return this.sites;
  }

  getSitesByProduct(availableProducts) {

    let groupSitesByProduct = {};

    this.sites.forEach((site) => {
      site.products.forEach((product) => {
        (groupSitesByProduct[product] = groupSitesByProduct[product] || []).push({
          cloudId: site.cloudId,
          displayName: site.displayName,
          url: site.url,
          avatarUrl: site.avatarUrl || '',
          isVertigo: site.isVertigo || false,
        })
      })
    });

    let result = [];
    for (var product in groupSitesByProduct) {

      if (product.length) {
        result.push({
          name: availableProducts[product].name,
          icon: availableProducts[product].login_logo_url || '',
          sites: groupSitesByProduct[product].map((site) => site.url)
        });
      }
    }
    return result;
  }

  getDisplayName() {
    return this.user.myDetails.name;
  }

  getFirstName(fullName = '') {
    return fullName.trim().split(' ')[0];
  }

  getLastName(fullName = '') {
    let lastName = '';
    let names = fullName.trim().split(' ');

    if (names.length > 1) {
      names.shift();
      lastName = names.join(' ');
    }

    return lastName;
  }

  getUserEmail() {
    return this.user.myDetails.email || '';
  }

  getUserAaid() {
    return this.user.myDetails.aaid || '';
  }

  getAvatar() {
    return this.user.myDetails.avatar;
  }

  isLoggedIn() {
    return this.user.isLoggedIn && this.user.myDetails.email.length > 0;
  }

  isExpert() {
    return this.organisationDetails.organisationType === 'EXPERT';
  }

  isReseller() {
    return this.organisationDetails.organisationType === 'RESELLER';
  }

  isPlatinumExpert() {
    return this.organisationDetails.organisationType === 'EXPERT' && this.organisationDetails.payOnAccount;
  }

  isValidSite(site) {

    const ignoreSiteNames = [
      'servicedog',
      'ecosystem',
      'atlassiantraining',
      'Atlassian Stride',
      'volunteerhub',
      'atlassiantraining',
      'hello-staging',
      'ecosystem',
      'riskmanagement',
      'hello-staging3',
      'hello-staging6',
      'hello-staging9',
      'jdog',
      'sre-ehlo',
      'trello',
      'Trello',
      'bitbucket',
      'Bitbucket'
    ];

    if (ignoreSiteNames.indexOf(site.displayName) > -1) {
      return false;
    }

    if (site.cloudId.startsWith('DUMMY')) {
      return false;
    }

    if (site.displayName.toUpperCase().match(".*-SANDBOX-\\d{3}$")) {
      return false
    }

    return true;
  }

}

export let hamletUser = new HamletUser();
